'use client;';

import { createContext, useCallback, useContext, useMemo } from 'react';
import { parseAsArrayOf, parseAsInteger, parseAsIsoDate, parseAsString, useQueryStates } from 'nuqs';

import { DateRangeValue } from '@/components/daterange';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';

export type CallTableFilters = {
  officeId: string;
  page: number;
  outcomes: string[];
  intents: string[];
  human_requested: string[];
  from_number: string;
  date_range: DateRangeValue | null;
};

interface ContextType {
  filters: CallTableFilters;
  setFilters: (filters: Partial<CallTableFilters>) => void;
}

const CallFiltersContext = createContext<ContextType | null>(null);

export function useCallFiltersCtx() {
  const ctx = useContext(CallFiltersContext);
  if (!ctx) {
    throw new Error('useCallFiltersCtx must be used within a CallFiltersProvider');
  }
  return ctx;
}

interface Props {
  children: React.ReactNode;
}

export function CallFiltersProvider({ children }: Props) {
  const { officeId: ctxOfficeId } = useOfficeCtx();
  const [query, setQuery] = useQueryStates(
    {
      officeId: parseAsString.withDefault(ctxOfficeId),
      page: parseAsInteger.withDefault(1),
      from_number: parseAsString.withDefault(''),
      to_date: parseAsIsoDate,
      from_date: parseAsIsoDate,
      intents: parseAsArrayOf(parseAsString, ',').withDefault([]),
      outcomes: parseAsArrayOf(parseAsString, ',').withDefault([]),
      human_requested: parseAsArrayOf(parseAsString, ',').withDefault([]),
    },
    {
      history: 'push',
      shallow: true,
    }
  );

  const filters = useMemo<CallTableFilters>(() => {
    return {
      officeId: query.officeId,
      page: query.page,
      date_range: query.from_date
        ? {
            from: query.from_date,
            to: query.to_date ?? undefined,
          }
        : null,
      from_number: query.from_number,
      intents: query.intents,
      outcomes: query.outcomes,
      human_requested: query.human_requested,
    };
  }, [query]);

  const onFiltersChange = useCallback(
    (filters: Partial<CallTableFilters>) => {
      setQuery((oldValue) => {
        const newValue = { ...oldValue, page: 1, ...filters };
        if (filters.date_range !== undefined) {
          newValue.from_date = filters.date_range?.from ?? null;
          newValue.to_date = filters.date_range?.to ?? null;
        }
        return newValue;
      });
    },
    [setQuery]
  );

  return (
    <CallFiltersContext.Provider value={{ filters, setFilters: onFiltersChange }}>
      {children}
    </CallFiltersContext.Provider>
  );
}
